import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DataSet, InvestmentType, Project, ProjectPhase } from 'src/app/interfaces/investmentcontrolling/inco-project';
import { IncoUserSearchResult } from 'src/app/interfaces/investmentcontrolling/inco-user-search-result';
import { DefaultValuesService } from 'src/app/services/defaultvalues.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-form-project-modal',
  templateUrl: './form-project-modal.component.html',
  styleUrls: ['./form-project-modal.component.less'],
  standalone: false
})
export class FormProjectModalComponent implements OnInit {
  form!: FormGroup;
  managementId!: number;
  isEditMode: boolean = false;
  currentProject!: Project;
  userUpn: string | undefined;
  isAdmin: boolean = false;
  defaultCategory!: string;

  projectPhases: ProjectPhase[] = [];
  investmentTypes: InvestmentType[] = [];
  multiprojects: DataSet[] = [];
  incoUsers: IncoUserSearchResult[] = [];
  initialFormValue: any;

  constructor(
    public dialogRef: MatDialogRef<FormProjectModalComponent>,
    private readonly formBuilder: FormBuilder,
    public defaults: DefaultValuesService,
    private readonly toastrService: ToastrService,

    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  ngOnInit(): void {
    this.managementId = this.data.managementId;
    this.isEditMode = this.data.isEditMode;
    this.userUpn = this.defaults.session?.UserUPN;
    this.isAdmin = this.defaults.isMersyAdminINCO;
    this.investmentTypes = this.data.investmentTypes;
    this.projectPhases = this.data.projectPhases;
    this.defaultCategory = this.data.defaultCategory;
    this.multiprojects = this.data.multiprojects.filter(mp => mp.isEnabled);
    this.incoUsers = this.data.incoUsers ?? [];
    this.initform();

    if (this.isEditMode && this.data.project) {
      this.currentProject = this.data.project;
      this.projectPhases = this.getAvailablePhasesForUser();
      this.ensureExistingResponsibleUserInList();
      this.fillForm();
    } else {
      this.applyDefaultCategory();
    }
    this.initialFormValue = this.form.getRawValue();
  }

  private ensureExistingResponsibleUserInList(): void {
    const current = this.currentProject?.responsibleUser;
    if (!current) return;
    const exists = this.incoUsers.some(u => u.mail === current);
    if (!exists) {
      this.incoUsers = [{ displayName: current, mail: current }, ...this.incoUsers];
    }
  }

  private getDefaultResponsibleUserMail(): string | null {
    if (!this.userUpn) return null;
    const upnLocal = this.userUpn.split('@')[0].toLowerCase();
    const match = this.incoUsers.find(u => u.mail?.toLowerCase() === this.userUpn!.toLowerCase()
      || u.mail?.toLowerCase().startsWith(upnLocal + '@'));
    return match ? match.mail : null;
  }

  initform(): void {
    this.form = this.formBuilder.group({
      id: 0,
      projectTitle: [null, [Validators.required, Validators.maxLength(200)]],
      responsibleUser: [{ value: this.getDefaultResponsibleUserMail(), disabled: !this.isAdmin }, Validators.required],
      researchDevelopment: [{ value: false, disabled: !this.isAdmin && this.isEditMode }],
      manufacturing: [{ value: false, disabled: !this.isAdmin && this.isEditMode }],
      fieldOperations: [{ value: false, disabled: !this.isAdmin && this.isEditMode }],
      informationTechnology: [{ value: false, disabled: !this.isAdmin && this.isEditMode }],
      bigR: [{ value: false, disabled: !this.isAdmin && this.isEditMode }],
      maintenance: [{ value: null, disabled: !this.isAdmin && this.isEditMode }, [Validators.required, Validators.min(0), Validators.max(100), this.maxOneDecimalValidator]],
      expansion: [{ value: null, disabled: !this.isAdmin && this.isEditMode }, [Validators.required, Validators.min(0), Validators.max(100), this.maxOneDecimalValidator]],
      focus: { value: false, disabled: !this.isAdmin && this.isEditMode },
      investmentTypeId: [{ value: '', disabled: !this.isAdmin && this.isEditMode }, Validators.required],
      phaseId: [{ value: null, disabled: !this.isAdmin && this.isEditMode }, Validators.required],
      multiprojectId: { value: null, disabled: !this.isAdmin && this.isEditMode },
      multiprojectName: { value: null, disabled: !this.isAdmin && this.isEditMode },
    }, { validators: this.atLeastOneBooleanSelectedValidator });
  }

  fillForm(): void {
    this.form.patchValue({
      id: this.currentProject.id,
      projectTitle: this.currentProject.projectTitle,
      responsibleUser: this.currentProject.responsibleUser,
      researchDevelopment: this.currentProject.researchDevelopment,
      manufacturing: this.currentProject.manufacturing,
      fieldOperations: this.currentProject.fieldOperations,
      informationTechnology: this.currentProject.informationTechnology,
      bigR: this.currentProject.bigR,
      maintenance: this.currentProject.maintenance,
      expansion: this.currentProject.expansion,
      focus: this.currentProject.focus,
      investmentTypeId: this.currentProject.investmentTypeId,
      phaseId: this.currentProject.phaseId,
      multiprojectId: this.currentProject.multiprojectId
    });

    if (!this.canEditPhase()) {
      this.form.get('phaseId')?.disable();
    }
  }

  // M/E accept one decimal place (e.g. 99.1), matching the DB decimal(5,2) columns.
  maxOneDecimalValidator: ValidatorFn = (control: AbstractControl) => {
    const value = control.value;
    if (value == null || value === '') return null;
    const num = Number(value);
    if (Number.isNaN(num)) return null;
    return Math.abs(num - Math.round(num * 10) / 10) < 1e-9 ? null : { maxOneDecimal: true };
  };

  atLeastOneBooleanSelectedValidator: ValidatorFn = (form: AbstractControl) => {
    const fields = [
      'researchDevelopment',
      'manufacturing',
      'fieldOperations',
      'informationTechnology',
      'bigR'
    ];
    const atLeastOne = fields.some(field => form.get(field)?.value === true);
    return atLeastOne ? null : { atLeastOneBoolean: true };
  };

  private applyDefaultCategory(): void {
    const categoryFieldMap: Record<string, string> = {
      'FLD': 'fieldOperations',
      'MFG': 'manufacturing',
      'R&D': 'researchDevelopment'
    };
    const field = categoryFieldMap[this.defaultCategory];
    if (field) {
      this.form.patchValue({ [field]: true });
    }
  }

  onMaintenanceChange(): void {
    const maintenance = this.form.get('maintenance')?.value ?? 0;
    // Round to 1 decimal so the pair always sums to exactly 100 (avoids float artifacts like 39.900000000000006).
    const expansion = Math.round((100 - maintenance) * 10) / 10;
    this.form.patchValue({ expansion: Math.max(expansion, 0) });
  }

  onExpansionChange(): void {
    const expansion = this.form.get('expansion')?.value ?? 0;
    const maintenance = Math.round((100 - expansion) * 10) / 10;
    this.form.patchValue({ maintenance: Math.max(maintenance, 0) });
  }

  canEditPhase(): boolean {
    if (this.isAdmin) {
      return true;
    }

    const currentPhase = this.projectPhases.find(phase => phase.id === this.currentProject.phaseId);
    if (!currentPhase) {
      return false;
    }

    switch (currentPhase.id) {
      case 1: // Backlog
      case 2: // Planning
      case 3: // Execution
        return true;
      case 4: // Closed
      case 5: // Canceled
        return false;
      default:
        return false;
    }

  }
  getAvailablePhasesForUser(): ProjectPhase[] {
    if (this.isAdmin) {
      return this.projectPhases;
    }

    const currentPhase = this.projectPhases.find(phase => phase.id === this.currentProject.phaseId);
    if (!currentPhase) {
      return [];
    }

    switch (currentPhase.id) {
      case 1: // Backlog
        return this.projectPhases;
      case 2: // Planning
        return this.projectPhases.filter(phase => phase.id !== 1);
      case 3: // Execution
        return this.projectPhases.filter(phase => phase.id === 3 || phase.id === 4 || phase.id === 5);
      case 4: // Closed
      case 5: // Canceled
        return [currentPhase];
      default:
        return [currentPhase];
    }
  }

  hasChanges(): boolean {
    return JSON.stringify(this.form.getRawValue()) !== JSON.stringify(this.initialFormValue);
  }

  onSubmit(): void {
    var a = this.form.getRawValue();
    if (this.form.valid && this.hasChanges()) {
      this.dialogRef.close(this.form.getRawValue());
    }
  }

  backButtonHandler(event: Event): void {
    // event.preventDefault();
    this.dialogRef.close();
  }
}
